import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { pendingSignatures, errorWords } from "@alm/domain";
import { RecordHeader } from "@/components/app/patterns/record-header";
import { RelativeTime } from "@/components/app/patterns/relative-time";
import { ResidentAvatar } from "@/components/app/clinical/resident-avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { saveFile } from "@/lib/files/download";
import { MoreHorizontal, Printer, Trash2 } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { exactTime } from "@/lib/facility/relative-time";
import { useZone } from "@/lib/facility/zone";
import { CodeStatusBadge } from "@/routes/_authenticated/_care/residents/-components/code-status-badge";
import { ActionsTaken } from "./-components/actions-taken";
import { DeleteIncidentDialog } from "./-components/delete-incident";
import { Fact } from "@/routes/_authenticated/_care/incidents/-components/incident-detail-parts-fact";
import { FactList } from "./-components/incident-detail-parts";
import { SignOffCard } from "@/routes/_authenticated/_care/incidents/-components/sign-off-card";
import { When } from "@/routes/_authenticated/_care/incidents/-components/incident-detail-parts-when";
import { IncidentForm, nowLocal } from "./-components/incident-form";
import { KindTile } from "@/routes/_authenticated/_care/incidents/-components/kind-tile";
import { SeverityChip } from "@/routes/_authenticated/_care/incidents/-components/severity-chip";
import { prettify } from "@/routes/_authenticated/_care/incidents/-components/incident-table-shared";
import { StatusText } from "@/routes/_authenticated/_care/incidents/-components/status-text";
import { LinkedReports } from "./-components/linked-reports";
import { MergedNotice } from "./-components/merged-notice";
import { SignDialog } from "./-components/sign-dialog";

export const Route = createFileRoute("/_authenticated/_care/incidents/$incidentId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      orpc.care.incidents.get.queryOptions({ input: { incidentId: params.incidentId } }),
    ),
  component: IncidentDetailPage,
});

const fail = (error: Error) => toast.error(errorWords(error));

const local = (value: string | Date | null | undefined) => {
  if (!value) return nowLocal();
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

function IncidentDetailPage() {
  const { incidentId } = Route.useParams();
  const canManage = useCan("incident:manage");
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [signing, setSigning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { data: incident } = useSuspenseQuery(
    orpc.care.incidents.get.queryOptions({ input: { incidentId } }),
  );
  const zone = useZone(incident.facilityId);
  /* Only for the face, room and code status beside the name; the report reads
     the same without it. */
  const resident = useQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId: incident.residentId } }),
  );
  const categories = useQuery(
    orpc.care.incidents.categories.list.queryOptions({ input: { includeInactive: false } }),
  );
  const queue = useQuery(orpc.care.incidents.signatures.queue.queryOptions({ input: {} }));
  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.care.incidents.key() });
  const print = useMutation(
    orpc.care.incidents.exportPdf.mutationOptions({ onSuccess: saveFile, onError: fail }),
  );
  const update = useMutation(
    orpc.care.incidents.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Incident updated");
        setEditing(false);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const sign = useMutation(
    orpc.care.incidents.signatures.sign.mutationOptions({
      onSuccess: async (result) => {
        setSigning(false);
        const problem = result.results.find((row) => row.error);
        if (problem) toast.error(problem.error ?? "Could not sign");
        else toast.success("Signed");
        await invalidate();
      },
      onError: fail,
    }),
  );

  const owed = pendingSignatures(incident);
  // A merged report is signed on the report it was merged into.
  const maySign = queue.data?.eligible && owed.length > 0 && !incident.mergedIntoId;
  const reporterOf = (id: string) =>
    incident.linkedReports.find((report) => report.id === id)?.reportedByName;
  const name = `${incident.residentFirstName} ${incident.residentLastName}`;
  const person = resident.data;
  const where = [
    person?.roomNumber ? `Room ${person.roomNumber}` : null,
    incident.facilityName,
  ].filter(Boolean);
  const closeOut = incident.edCloseSignedAt
    ? ("done" as const)
    : !incident.followUpRequired
      ? ("skipped" as const)
      : incident.status === "resolved"
        ? ("owed" as const)
        : ("later" as const);

  return (
    <>
      <RecordHeader
        back={{ label: "Incidents", link: { to: "/incidents" } }}
        media={<KindTile kind={incident.incidentType} size="lg" />}
        title={prettify(incident.incidentType)}
        titleId="incident-title"
        /* Severity and status in the register's marks, outside the h1 so the
           heading reads as the incident rather than "Fall High Open". */
        badges={
          <>
            <SeverityChip severity={incident.severity} suffix="severity" />
            <StatusText status={incident.status} />
          </>
        }
        facts={[
          <span key="reported">
            Reported <RelativeTime value={incident.reportedAt} timeZone={zone} />
            {incident.reportedByName ? ` by ${incident.reportedByName}` : ""}
          </span>,
        ]}
        actions={
          <>
            <Button
              variant="outline"
              disabled={print.isPending}
              onClick={() => print.mutate({ incidentId })}
            >
              <Printer className="size-4" />
              Print report
            </Button>
            {canManage ? (
              <Button variant="outline" onClick={() => setEditing(true)}>
                Edit incident
              </Button>
            ) : null}
            {/* The one primary sits just before the menu, and first on a phone, where the row
                wraps. */}
            {maySign ? (
              <Button className="max-sm:order-first" onClick={() => setSigning(true)}>
                Sign incident
              </Button>
            ) : null}
            {/* An incident opened on the wrong resident had nothing but an
                edit to fix it, which leaves it in everyone's queue. Rare and
                permanent, so it waits behind the menu. */}
            {canManage ? (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="More actions for this incident">
                    <MoreHorizontal className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem variant="destructive" onSelect={() => setDeleting(true)}>
                    <Trash2 />
                    Delete incident
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </>
        }
      />
      {incident.mergedIntoId ? (
        <MergedNotice
          incidentId={incidentId}
          mergedIntoId={incident.mergedIntoId}
          canManage={canManage}
        />
      ) : null}
      <div className="grid items-start gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          <Card className="gap-0 py-0">
            <CardContent className="flex flex-col gap-4 py-5">
              <div className="flex items-center gap-3">
                <ResidentAvatar
                  size="default"
                  firstName={incident.residentFirstName}
                  lastName={incident.residentLastName}
                  photoUrl={person?.photoUrl}
                  codeStatus={person?.codeStatus}
                  hideCodeStatusLabel
                />
                <div className="min-w-0">
                  <span className="flex flex-wrap items-center gap-2">
                    <Link
                      to="/residents/$residentId"
                      params={{ residentId: incident.residentId }}
                      className="focus-ring rounded-sm font-medium hover:underline"
                    >
                      {name}
                    </Link>
                    <CodeStatusBadge codeStatus={person?.codeStatus} />
                  </span>
                  {where.length ? (
                    <p className="text-muted-foreground text-sm">{where.join(", ")}</p>
                  ) : null}
                </div>
              </div>
              <h2 className="sr-only">What happened</h2>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{incident.description}</p>
              <FactList>
                <Fact label="When">
                  <When value={incident.reportedAt} zone={zone} />
                </Fact>
                <Fact label="Follow-up">
                  {incident.followUpRequired
                    ? "Required, needs a close-out signature"
                    : "Not required"}
                </Fact>
                <Fact label="Resolved">
                  {incident.resolvedAt ? <When value={incident.resolvedAt} zone={zone} /> : "-"}
                </Fact>
              </FactList>
            </CardContent>
          </Card>
          <ActionsTaken
            incidentId={incidentId}
            actions={incident.actions}
            zone={zone}
            canManage={canManage}
            reporterOf={reporterOf}
          />
        </div>
        <SignOffCard
          steps={[
            {
              key: "ack",
              title: "Acknowledgement",
              state: incident.edAckSignedAt ? "done" : "owed",
              detail: incident.edAckSignedAt
                ? `${incident.edAckSignedByName}, ${exactTime(incident.edAckSignedAt, zone)}`
                : "Waiting on the executive director",
            },
            {
              key: "close",
              title: "Close-out",
              state: closeOut,
              detail:
                closeOut === "done"
                  ? `${incident.edCloseSignedByName}, ${exactTime(incident.edCloseSignedAt ?? "", zone)}`
                  : closeOut === "skipped"
                    ? "Not required for this incident"
                    : closeOut === "owed"
                      ? "Waiting on the executive director"
                      : "Owed once the incident is resolved",
            },
          ]}
          note={
            maySign
              ? `Your signature is owed on the ${owed.map((phase) => (phase === "ack" ? "acknowledgement" : "close-out")).join(" and the ")}.`
              : undefined
          }
        />
      </div>
      {incident.mergedIntoId ? null : <LinkedReports incident={incident} canManage={canManage} />}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit incident</DialogTitle>
          </DialogHeader>
          <IncidentForm
            showResident={false}
            defaultValues={{
              residentId: incident.residentId,
              incidentType: incident.incidentType,
              severity: incident.severity as "low" | "medium" | "high" | "critical",
              description: incident.description,
              status: incident.status as "open" | "in_progress" | "resolved",
              followUpRequired: incident.followUpRequired,
              reportedAt: local(incident.reportedAt),
            }}
            residents={[]}
            categories={(categories.data ?? []).map((row) => row.name)}
            pending={update.isPending}
            onCancel={() => setEditing(false)}
            onSubmit={(values) =>
              update.mutate({
                incidentId,
                incidentType: values.incidentType,
                severity: values.severity,
                description: values.description,
                status: values.status,
                followUpRequired: values.followUpRequired,
                reportedAt: new Date(values.reportedAt),
              })
            }
          />
        </DialogContent>
      </Dialog>
      <DeleteIncidentDialog incidentId={incidentId} open={deleting} onOpenChange={setDeleting} />
      <SignDialog
        open={signing}
        count={owed.length}
        pending={sign.isPending}
        onOpenChange={setSigning}
        onSign={(password) =>
          sign.mutate({ password, items: owed.map((phase) => ({ incidentId, phase })) })
        }
      />
    </>
  );
}
